import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";

// HOOKS
import useScreenSize from "../../hooks/useScreenSize";
import { useModalBackGesture } from "../../hooks/useModalBackGesture";

// REDUX
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import {
  closeCryptoModal,
  updateCryptoSelectedAccount,
  updateCryptoStep,
} from "../../store/slices/cryptoSlice";

// TYPES
import { CryptoAccount } from "./types";

// CONNECTORS
import { getTermsAndConditionsStatus } from "../../connectors/cryptoApi";

// CONSTANTS
import { MOBILE_OPERATE_WIDTH } from "../OperateMEP/constants";

// COMPONENTS
import { ModalContent } from "@nextui-org/react";
import CustomModal from "../CustomModal/CustomModal";
import AccountsModal from "./ui/AccountsModal/AccountsModal";
import RedirectModal from "./ui/RedirectModal/RedirectModal";
import DetailsModal from "./ui/DetailsModal/DetailsModal";
import ConfirmModal from "./ui/ConfirmModal/ConfirmModal";
import StatusModal from "./ui/StatusModal/StatusModal";
import FeedbackModal from "./ui/FeedbackModal/FeedbackModal";
import JointSignatureModal from "./ui/JointSignatureModal/JointSignatureModal";

const companyName = "Allaria Digital";

const CryptoSection = () => {
  const dispatch = useAppDispatch();
  const { step, openModal } = useAppSelector((state) => state.crypto);
  const { accounts } = useAppSelector((state) => state.userAccounts);
  const [isSingleAccount, setIsSingleAccount] = useState(false);
  const [singleAccountHasJS, setSingleAccountHasJS] = useState<boolean | null>(
    null
  );
  const { isMobile } = useScreenSize(MOBILE_OPERATE_WIDTH);
  const { user } = useAppSelector((state) => state.authentication);

  const isCryptoOnly =
    user?.authorization?.crypto?.roles?.includes("CRYPTO_ONLY");

  const isCryptoUser = user?.authorization?.crypto?.roles?.includes("VIEW_ALL");

  const [isRedirect, setIsRedirect] = useState(false);

  const fetchOptions = async () => {
    const res = await getTermsAndConditionsStatus();
    const options =
      accounts?.map((acc) => {
        const account = res.find(
          (account) => account.accountId === Number(acc.number)
        );
        return {
          number: acc.number,
          legalName: acc.name,
          acceptedTerms: account?.accepted ?? false,
          isJointSignature: account?.isJointSignature,
          isTitular: account?.isTitular,
        };
      }) ?? [];
    return options as CryptoAccount[];
  };

  const { data: accountOptions, refetch: refetchTermsAndConditionsStatus } =
    useQuery({
      queryKey: ["accountOptions", accounts],
      queryFn: fetchOptions,
      enabled: !!accounts?.length && (isCryptoUser || isCryptoOnly),
      staleTime: Infinity,
      refetchOnWindowFocus: false,
    });

  // If is single account:
  //    If account has not accepted t&c, redirect to details. Else, redirect to AllariaCrypto
  useEffect(() => {
    if (accountOptions?.length === 1 && step === "ACCOUNTS") {
      if (isCryptoOnly && isRedirect) return;

      const singleAccount = accountOptions?.[0];

      setIsSingleAccount(true);
      setSingleAccountHasJS(singleAccount.isJointSignature);
      dispatch(updateCryptoSelectedAccount(singleAccount.number));

      if (singleAccount.acceptedTerms) {
        dispatch(updateCryptoStep("REDIRECT"));
        setIsRedirect(true);
      } else {
        dispatch(updateCryptoStep("DETAILS"));
      }
    }
  }, [accountOptions, step, isRedirect, isCryptoOnly]);

  const onClose = () => {
    dispatch(closeCryptoModal());
    dispatch(updateCryptoStep("ACCOUNTS"));
  };

  // CRYPTO_ONLY users cannot dismiss the modal — disable back gesture for them
  useModalBackGesture({
    isOpen: openModal && !isCryptoOnly,
    onBack: onClose,
  });

  const getComponent = () => {
    switch (step) {
      case "ACCOUNTS":
        return (
          <AccountsModal
            companyName={companyName}
            accountOptions={accountOptions || []}
            onClose={onClose}
            isCryptoOnly={isCryptoOnly ?? false}
          />
        );
      case "DETAILS":
        return (
          <DetailsModal
            companyName={companyName}
            isSingleAccount={isSingleAccount}
            onClose={onClose}
            singleAccountHasJS={singleAccountHasJS}
          />
        );
      case "CONFIRM":
        return (
          <ConfirmModal
            companyName={companyName}
            refetch={async () => {
              await refetchTermsAndConditionsStatus();
            }}
          />
        );
      case "STATUS":
        return <StatusModal companyName={companyName} onClose={onClose} />;
      case "REDIRECT":
        return (
          <RedirectModal
            companyName={companyName}
            onClose={onClose}
            isCryptoOnly={isCryptoOnly ?? false}
          />
        );
      case "FEEDBACK":
        return <FeedbackModal companyName={companyName} />;
      case "JOINT_SIGNATURE":
        return (
          <JointSignatureModal
            onClose={onClose}
            isCryptoOnly={isCryptoOnly ?? false}
          />
        );
    }
  };

  return (
    <CustomModal
      onClose={isCryptoOnly ? undefined : () => dispatch(closeCryptoModal())}
      isOpen={openModal}
      classNames={{
        base: `${!isMobile ? "p-1.5 min-w-[35rem] max-h-[33rem]" : "p-0 min-w-[100%] min-h-[100%] h-screen flex flex-col"}`,
      }}
      isDismissable={false}
      hideCloseButton={isMobile || isCryptoOnly || step === "DETAILS"}
    >
      <ModalContent>{getComponent()}</ModalContent>
    </CustomModal>
  );
};

export default CryptoSection;
